import { ScopedPaletteResponseEntry } from '../models';

export const SCOPE_KEY_PREFIX = 'scoped-palette';

export type ScopeLevel = 'method' | 'eduContentBook' | 'learningDomain' | 'eduContentTOC';

export interface ScopeLevelDescriptor {
  readonly level: ScopeLevel;
  readonly entryIdField: 'methodId' | 'eduContentBookId' | 'eduContentTOCId' | 'learningDomainId';
  readonly responseField: 'methods' | 'eduContentBooks' | 'eduContentTOCs' | 'learningDomains';
  readonly queryPrefix: 'method' | 'book' | 'toc' | 'learningDomain';
  // Class-repetition count for the injected selector at the given TOC depth.
  // method/book ignore depth (the chain only branches at the toc/LD level).
  // For toc/LD, deeper TOCs win: each depth step adds 2 repetitions so a
  // child toc (and its LD) outranks a parent toc (and its LD).
  readonly repetitionFor: (depth: number) => number;
  readonly routeParamAliases: readonly string[];
}

export const SCOPE_LEVELS: Record<ScopeLevel, ScopeLevelDescriptor> = {
  method: {
    level: 'method',
    entryIdField: 'methodId',
    responseField: 'methods',
    queryPrefix: 'method',
    repetitionFor: () => 1,
    routeParamAliases: ['methodId', 'method'],
  },
  eduContentBook: {
    level: 'eduContentBook',
    entryIdField: 'eduContentBookId',
    responseField: 'eduContentBooks',
    queryPrefix: 'book',
    repetitionFor: () => 2,
    routeParamAliases: ['eduContentBookId', 'book', 'bookId'],
  },
  learningDomain: {
    level: 'learningDomain',
    entryIdField: 'learningDomainId',
    responseField: 'learningDomains',
    queryPrefix: 'learningDomain',
    repetitionFor: (depth: number) => 3 + 2 * depth,
    routeParamAliases: [],
  },
  eduContentTOC: {
    level: 'eduContentTOC',
    entryIdField: 'eduContentTOCId',
    responseField: 'eduContentTOCs',
    queryPrefix: 'toc',
    repetitionFor: (depth: number) => 4 + 2 * depth,
    routeParamAliases: ['eduContentTOCId', 'toc', 'tocId'],
  },
};

// Ordered most-specific → least-specific, so a response entry with several ids
// populated is classified as the narrowest level present.
export const SCOPE_LEVELS_ORDERED: readonly ScopeLevelDescriptor[] = [
  SCOPE_LEVELS.eduContentTOC,
  SCOPE_LEVELS.learningDomain,
  SCOPE_LEVELS.eduContentBook,
  SCOPE_LEVELS.method,
];

export function getSelectorRepetition(level: ScopeLevel, depth = 0): number {
  return SCOPE_LEVELS[level].repetitionFor(depth);
}

const LD_NOT_LOADABLE_MESSAGE = 'learningDomain palettes cannot be loaded directly; they arrive with parent loads.';

// Narrows `ScopeLevel` to the levels that have their own API endpoint. Throws
// for `learningDomain`, which only ever arrives in the response of a parent
// load. Centralised here so the error text and unreachability contract have
// one source of truth — both `apiCall` and `queryForLevel` route through it.
export function assertLoadable(level: ScopeLevel): asserts level is Exclude<ScopeLevel, 'learningDomain'> {
  if (level === 'learningDomain') throw new Error(LD_NOT_LOADABLE_MESSAGE);
}

export function methodQuery(methodId: number): string {
  return `${SCOPE_LEVELS.method.queryPrefix}:${methodId}`;
}

// `includeLearningDomains` appends a `:with-lds` suffix when true so the
// dedup key distinguishes a book-with-LDs fetch from the default no-LDs fetch.
export function bookQuery(
  eduContentBookId: number,
  includeChildren: boolean,
  includeLearningDomains = false
): string {
  const childrenSeg = includeChildren ? 'children' : 'no-children';
  let query = `${SCOPE_LEVELS.eduContentBook.queryPrefix}:${eduContentBookId}:${childrenSeg}`;
  if (includeLearningDomains) query += ':with-lds';
  return query;
}

// `includeLearningDomains` appends a `:with-lds` suffix when true so the dedup
// key distinguishes it from the default no-LDs fetch.
// `includeAncestors` (toc-level only) appends a `:with-ancestors` suffix so the
// "deep cascade" fetch is a distinct dedup key from the single-toc fetch.
export function tocQuery(
  eduContentTOCId: number,
  includeChildren: boolean,
  includeLearningDomains = false,
  includeAncestors = false
): string {
  const childrenSeg = includeChildren ? 'children' : 'no-children';
  let query = `${SCOPE_LEVELS.eduContentTOC.queryPrefix}:${eduContentTOCId}:${childrenSeg}`;
  if (includeLearningDomains) query += ':with-lds';
  if (includeAncestors) query += ':with-ancestors';
  return query;
}

// Builds the canonical query signature for a (level, id, flags) tuple.
// `includeChildren` is ignored at the method level (the API has no such variant).
// `includeLearningDomains` is honored at the book and toc levels; `includeAncestors`
// is only honored at the toc level — those are the only API call variants that
// support them.
// Throws on `learningDomain` (via `assertLoadable`) because LD palettes always
// arrive with parent loads — they are never fetched directly.
export function queryForLevel(
  level: ScopeLevel,
  id: number,
  includeChildren = false,
  includeLearningDomains = false,
  includeAncestors = false
): string {
  assertLoadable(level);
  switch (level) {
    case 'method':
      return methodQuery(id);
    case 'eduContentBook':
      return bookQuery(id, includeChildren, includeLearningDomains);
    case 'eduContentTOC':
      return tocQuery(id, includeChildren, includeLearningDomains, includeAncestors);
  }
}

// A query "covers" an id when any variant for that id has been fetched —
// e.g. `book:5:children` AND `book:5:no-children` both cover book 5. Used by
// `ensureLoadedFor` to avoid firing a second request for data we already have.
// Accepts any iterable of query strings (array, Set, or `Object.keys(record)`).
export function isIdCovered(queries: Iterable<string>, level: ScopeLevel, id: number): boolean {
  const prefix = `${SCOPE_LEVELS[level].queryPrefix}:${id}`;
  for (const query of queries) {
    if (query === prefix || query.startsWith(`${prefix}:`)) return true;
  }
  return false;
}

export function levelOfEntry(entry: ScopedPaletteResponseEntry): ScopeLevelDescriptor | undefined {
  return SCOPE_LEVELS_ORDERED.find((descriptor) => entry[descriptor.entryIdField] != null);
}
